import { useState } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Mail, Search } from 'lucide-react'
import {
  ETICHETA_STARE_TASK,
  numeDepartament,
  type DepartamentSlug,
  type StareTask,
  esteNotaArhivare,
} from '@b4l/shared'
import { api } from '../../lib/api'
import { StareLista } from '../../components/ui/StareLista'
import { Explicatie } from '../../components/ui/Explicatie'
import { Button } from '../../components/ui/Button'
import { useDebounced } from '../../hooks/useDebounced'
import { Avatar, EtichetaDepartament, MotiveCautare } from './CardFata'
import { CULOARE_DEPARTAMENT } from './constante'
import { CHEI, oraScurt, zileDeAzi, ziScurt, type MotivCautare, type Scop } from './date'

// ─── Activitate ──────────────────────────────────────────────────────────────

interface EvenimentFlux {
  tip: 'stare' | 'trimis' | 'comentariu'
  cand: string
  cine: { id: number; name: string } | null
  din: string | null
  spre: string | null
  nota: string | null
  task: { id: number; titlu: string; departament: DepartamentSlug | null }
}

function ceAFacut(e: EvenimentFlux): string {
  if (e.tip === 'comentariu') return 'a comentat la'
  if (e.tip === 'trimis') return `a trimis la ${numeDepartament(e.spre ?? '')}`
  switch (e.spre) {
    case 'de_preluat':
      return e.din ? 'a lăsat înapoi la „De preluat”' : 'a intrat la „De preluat”:'
    case 'todo':
      return e.din === 'done' ? 'a redeschis' : 'a preluat'
    case 'in_progress':
      return e.din === 'in_asteptare'
        ? 'a reluat'
        : e.din === 'de_preluat'
          ? 'a preluat'
          : e.din === 'done'
            ? 'a redeschis'
            : 'a început'
    case 'in_asteptare':
      return 'a pus în așteptare'
    case 'done':
      return 'a terminat'
    case 'cancelled':
      return esteNotaArhivare(e.nota) ? 'a arhivat' : 'a respins'
    default:
      return `a mutat în ${ETICHETA_STARE_TASK[e.spre as StareTask] ?? e.spre}`
  }
}

function numeZi(iso: string): string {
  const d = zileDeAzi(iso)
  if (d === 0) return 'Azi'
  if (d === -1) return 'Ieri'
  return new Date(iso).toLocaleDateString('ro-RO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
}

/** Ce s-a lucrat, in ordine. Raspunde la „ce s-a facut azi?" fara sa intrebe nimeni pe nimeni. */
export function VedereActivitate({
  scop,
  laDeschidere,
}: {
  scop: Scop | undefined
  laDeschidere: (id: number) => void
}) {
  const q = useQuery<{ flux: EvenimentFlux[] }>({
    queryKey: CHEI.activitate(scop),
    queryFn: async () =>
      (await api.get('/tasks/activitate', { params: { ...(scop ? { dep: scop } : {}), zile: 7 } }))
        .data,
  })
  const zile = new Map<string, EvenimentFlux[]>()
  for (const e of q.data?.flux ?? []) {
    const k = numeZi(e.cand)
    zile.set(k, [...(zile.get(k) ?? []), e])
  }
  const multe = scop === 'toate' || scop === 'trimise'
  return (
    <div className="grid gap-3.5">
      <p className="text-corp text-b4l-text-muted">
        Ce s-a lucrat în ultimele 7 zile, în ordine — mutări, cereri și comentarii.
      </p>
      <StareLista interogare={q} gol={zile.size === 0} mesajGol="Nicio mișcare în ultimele 7 zile.">
        {[...zile.entries()].map(([zi, ev]) => (
          <section key={zi} className="grid gap-1.5">
            <h3 className="text-eticheta font-semibold uppercase tracking-wide text-b4l-text-dim first-letter:uppercase">
              {zi}
            </h3>
            <div className="rounded-caseta border border-b4l-border bg-b4l-surface p-1">
              {ev.map((e, i) => (
                <button
                  key={`${e.tip}-${e.cand}-${i}`}
                  type="button"
                  onClick={() => laDeschidere(e.task.id)}
                  className="grid w-full grid-cols-[44px_22px_1fr] items-start gap-2.5 rounded-md px-2.5 py-1.5 text-left hover:bg-b4l-bg-soft"
                >
                  <span className="pt-0.5 text-mic tabular-nums text-b4l-text-dim">
                    {oraScurt(e.cand)}
                  </span>
                  {e.cine ? (
                    <Avatar nume={e.cine.name} />
                  ) : (
                    <span
                      className="grid h-5 w-5 place-items-center rounded-full bg-b4l-bg text-b4l-text-dim"
                      aria-hidden="true"
                    >
                      <Mail size={11} />
                    </span>
                  )}
                  <span className="min-w-0 text-corp text-b4l-text">
                    {e.cine && <b className="font-semibold">{e.cine.name} </b>}
                    {!e.cine && <span className="text-b4l-text-muted">Automat: </span>}
                    {ceAFacut(e)} <b className="font-semibold">{e.task.titlu}</b>
                    {(multe || e.task.departament == null) && e.task.departament && (
                      <span className="text-b4l-text-dim">
                        {' '}
                        · {numeDepartament(e.task.departament)}
                      </span>
                    )}
                    {e.nota && (
                      <span className="block text-mic text-b4l-text-muted">„{e.nota}”</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </section>
        ))}
      </StareLista>
    </div>
  )
}

// ─── Arhiva ──────────────────────────────────────────────────────────────────

interface RandArhiva {
  id: number
  titlu: string
  stare: 'done' | 'cancelled'
  /** Închis cu „Arhivează” (rămâne în istoric), nu respins. */
  arhivat: boolean
  inchis: string | null
  /** Fara data de inchidere (inchise inainte de 12.10.2026): ultima modificare, aproximativ. */
  modificat: string | null
  departament: DepartamentSlug | null
  sursa: DepartamentSlug | null
  cine: { id: number; name: string } | null
  zile: number | null
  nota: string | null
  /** La cautare: de ce a iesit, cand nu se vede din titlu. */
  motive?: MotivCautare[]
}

/**
 * Tot ce s-a terminat, cu cautare. Se incarca doar cand o deschizi, cate 20 — tabloul ramane
 * usor (ca auto-arhivarea din Linear: inchisele pleaca, dar raman cautabile).
 */
export function VedereArhiva({
  scop,
  laDeschidere,
}: {
  scop: Scop | undefined
  laDeschidere: (id: number) => void
}) {
  const [text, setText] = useState('')
  const [stare, setStare] = useState<'done' | 'arhivate' | 'cancelled' | 'toate'>('done')
  const cautare = useDebounced(text.trim(), 300)
  const parametri = { ...(scop ? { dep: scop } : {}), ...(cautare ? { q: cautare } : {}), stare }
  const q = useInfiniteQuery<{
    total: number
    page: number
    pageSize: number
    maiMulte?: boolean
    randuri: RandArhiva[]
  }>({
    queryKey: CHEI.arhiva(parametri),
    queryFn: async ({ pageParam }) =>
      (await api.get('/tasks/arhiva', { params: { ...parametri, page: pageParam, pageSize: 20 } }))
        .data,
    initialPageParam: 1,
    getNextPageParam: (ultima) =>
      ultima.page * ultima.pageSize < ultima.total ? ultima.page + 1 : undefined,
  })
  const randuri = q.data?.pages.flatMap((p) => p.randuri) ?? []
  const total = q.data?.pages[0]?.total ?? 0
  const luni = new Map<string, RandArhiva[]>()
  for (const r of randuri) {
    // Cele fara data de inchidere vin ultimele, de la server, sub un singur antet: lunile lor ar fi
    // ghicite din ultima modificare si s-ar amesteca cu lunile adevarate.
    const k = r.inchis
      ? new Date(r.inchis).toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' })
      : 'Închise înainte de trecerea pe departamente (fără dată de închidere)'
    luni.set(k, [...(luni.get(k) ?? []), r])
  }
  const multe = scop === 'toate' || scop === 'trimise'

  return (
    <div className="grid gap-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative min-w-[220px] flex-1">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-b4l-text-dim"
            aria-hidden="true"
          />
          <input
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Caută în tot ce s-a terminat — titlu, detalii, oameni, #nr"
            aria-label="Caută în arhivă"
            className="w-full rounded-caseta border border-b4l-border bg-b4l-surface py-2 pl-9 pr-3 text-corp text-b4l-text"
          />
        </label>
        <div
          className="inline-flex gap-0.5 rounded-caseta bg-b4l-bg p-0.5"
          role="group"
          aria-label="Ce arăt"
        >
          {(
            [
              ['done', 'Terminate'],
              ['arhivate', 'Arhivate'],
              ['cancelled', 'Respinse'],
              ['toate', 'Toate'],
            ] as const
          ).map(([k, e]) => (
            <button
              key={k}
              type="button"
              aria-pressed={stare === k}
              onClick={() => setStare(k)}
              className={`rounded-md px-2.5 py-1 text-mic font-medium ${
                stare === k
                  ? 'bg-b4l-surface text-b4l-text shadow-sm'
                  : 'text-b4l-text-muted hover:text-b4l-text'
              }`}
            >
              {e}
            </button>
          ))}
        </div>
        {q.isSuccess && (
          <span className="text-mic tabular-nums text-b4l-text-dim">
            {total} {total === 1 ? 'task' : 'task-uri'}
            {q.data?.pages[0]?.maiMulte && ' (primele — scrie încă un cuvânt)'}
          </span>
        )}
      </div>
      <StareLista
        interogare={q}
        gol={randuri.length === 0}
        mesajGol={cautare ? 'Nimic găsit. Încearcă alt cuvânt.' : 'Nimic încheiat încă.'}
      >
        {[...luni.entries()].map(([luna, l]) => (
          <section key={luna} className="grid gap-1">
            <h3 className="text-eticheta font-semibold uppercase tracking-wide text-b4l-text-dim">
              {luna} <span className="tabular-nums">· {l.length}</span>
            </h3>
            <div className="divide-y divide-b4l-border rounded-caseta border border-b4l-border bg-b4l-surface">
              {l.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => laDeschidere(r.id)}
                  className="grid w-full grid-cols-[64px_1fr_auto] items-center gap-3 px-3 py-2 text-left hover:bg-b4l-bg-soft max-sm:grid-cols-1 max-sm:gap-1"
                >
                  <span className="text-mic tabular-nums text-b4l-text-dim">
                    {r.inchis ? (
                      ziScurt(r.inchis)
                    ) : r.modificat ? (
                      <Explicatie text="Închis înainte de 12.10.2026, când nu se ținea data închiderii. E data ultimei modificări.">
                        ~{ziScurt(r.modificat)}
                      </Explicatie>
                    ) : (
                      '—'
                    )}
                  </span>
                  <span className="grid min-w-0 gap-1">
                    <span
                      className={`text-corp font-medium ${
                        r.stare === 'cancelled'
                          ? r.arhivat
                            ? 'text-b4l-text-muted'
                            : 'text-b4l-text-muted line-through'
                          : 'text-b4l-text'
                      }`}
                    >
                      {r.titlu}
                    </span>
                    {r.motive && r.motive.length > 0 && <MotiveCautare motive={r.motive} />}
                    <span className="flex flex-wrap gap-1.5">
                      {multe && r.departament && <EtichetaDepartament slug={r.departament} />}
                      {r.sursa && (
                        <span className="rounded-full bg-b4l-purple-light px-2 py-px text-eticheta font-semibold text-b4l-purple-dark">
                          de la {numeDepartament(r.sursa)}
                        </span>
                      )}
                      {r.stare === 'cancelled' ? (
                        <span className="rounded-full bg-b4l-bg px-2 py-px text-eticheta font-semibold text-b4l-text-muted">
                          {r.arhivat ? 'arhivat' : 'respins'}
                        </span>
                      ) : (
                        r.zile != null && (
                          <span className="rounded-full bg-b4l-bg px-2 py-px text-eticheta font-semibold text-b4l-text-muted">
                            în {r.zile} {r.zile === 1 ? 'zi' : 'zile'}
                          </span>
                        )
                      )}
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-mic text-b4l-text-muted">
                    {r.cine && <Avatar nume={r.cine.name} />}
                    {r.cine?.name ?? '—'}
                  </span>
                </button>
              ))}
            </div>
          </section>
        ))}
        {q.hasNextPage && (
          <Button
            variant="secondary"
            className="justify-self-center"
            loading={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
          >
            Încarcă încă {Math.min(20, total - randuri.length)} (din {total - randuri.length}{' '}
            rămase)
          </Button>
        )}
      </StareLista>
      <p className="text-mic text-b4l-text-dim">
        Tot ce trece de 7 zile pleacă de pe tablou și rămâne aici. Se încarcă doar când deschizi
        Arhiva, câte 20.
      </p>
    </div>
  )
}

// ─── Ansamblu (admin) ────────────────────────────────────────────────────────

interface AnsambluDep {
  slug: DepartamentSlug
  nume: string
  stari: Partial<Record<StareTask, number>>
  depasite: number
  gata7zile: number
  nepreluateDeMult: number
  incarcare: { id: number; nume: string; deschise: number }[]
}

const SEGMENTE: { stare: StareTask | 'gata'; nume: string; culoare: string }[] = [
  { stare: 'de_preluat', nume: 'De preluat', culoare: 'bg-b4l-amber' },
  { stare: 'in_progress', nume: 'În lucru', culoare: 'bg-b4l-blue' },
  { stare: 'in_asteptare', nume: 'Așteaptă', culoare: 'bg-b4l-purple' },
  { stare: 'gata', nume: 'Gata, 7 zile', culoare: 'bg-b4l-green' },
]

/** Pentru admini: unde se aduna munca. Incarcarea pe om e pentru echilibrare — nu e clasament. */
export function VedereAnsamblu({
  scop,
  laAlegere,
}: {
  scop: Scop | undefined
  laAlegere: (d: DepartamentSlug) => void
}) {
  const q = useQuery<{ departamente: AnsambluDep[] }>({
    queryKey: CHEI.ansamblu,
    queryFn: async () => (await api.get('/tasks/ansamblu')).data,
  })
  const lista = (q.data?.departamente ?? []).filter(
    (d) => !scop || scop === 'toate' || scop === 'trimise' || d.slug === scop,
  )
  return (
    <div className="grid gap-3">
      <p className="text-corp text-b4l-text-muted">
        Unde se adună munca, pe departament. Încărcarea pe fiecare coleg apare ca să se poată
        echilibra — nu e un clasament.
      </p>
      <StareLista interogare={q} gol={lista.length === 0} mesajGol="Niciun departament.">
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(290px,1fr))]">
          {lista.map((d) => {
            // Cele vechi „De făcut" se numara „În lucru" (14.10.2026).
            const n = (s: StareTask | 'gata') =>
              s === 'gata'
                ? d.gata7zile
                : s === 'in_progress'
                  ? (d.stari.in_progress ?? 0) + (d.stari.todo ?? 0)
                  : (d.stari[s] ?? 0)
            const tot = SEGMENTE.reduce((s, x) => s + n(x.stare), 0) || 1
            const max = Math.max(4, n('de_preluat'), ...d.incarcare.map((o) => o.deschise))
            return (
              <article
                key={d.slug}
                className={`grid gap-2.5 rounded-caseta border border-l-4 border-b4l-border bg-b4l-surface p-3.5 ${CULOARE_DEPARTAMENT[d.slug].bara}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="text-sectiune font-semibold text-b4l-text">{d.nume}</h3>
                  <button
                    type="button"
                    onClick={() => laAlegere(d.slug)}
                    className="text-mic text-b4l-blue hover:underline"
                  >
                    Vezi tabloul →
                  </button>
                </div>
                <div className="flex h-2.5 overflow-hidden rounded-md bg-b4l-bg" aria-hidden="true">
                  {SEGMENTE.map((s) => (
                    <span
                      key={s.stare}
                      className={s.culoare}
                      style={{ width: `${(100 * n(s.stare)) / tot}%` }}
                    />
                  ))}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-mic tabular-nums text-b4l-text-muted">
                  {SEGMENTE.map((s) => (
                    <span key={s.stare} className="inline-flex items-center gap-1">
                      <i
                        className={`inline-block h-2 w-2 rounded-sm ${s.culoare}`}
                        aria-hidden="true"
                      />
                      {s.nume} {n(s.stare)}
                    </span>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-2 tabular-nums">
                  <Cifra
                    eticheta="Depășite"
                    valoare={d.depasite}
                    ton={d.depasite ? 'rosu' : undefined}
                  />
                  <Cifra
                    eticheta="Nepreluate 2+ zile"
                    valoare={d.nepreluateDeMult}
                    ton={d.nepreluateDeMult ? 'galben' : undefined}
                  />
                  <Cifra eticheta="Gata, 7 zile" valoare={d.gata7zile} />
                </div>
                <div className="grid gap-1.5">
                  <span className="text-eticheta font-semibold uppercase tracking-wide text-b4l-text-dim">
                    Task-uri deschise, pe colegi
                  </span>
                  {d.incarcare.length === 0 && n('de_preluat') === 0 && (
                    <span className="text-mic text-b4l-text-dim">Nimic deschis.</span>
                  )}
                  {[...d.incarcare]
                    .sort((a, b) => a.nume.localeCompare(b.nume, 'ro'))
                    .map((o) => (
                      <Bara key={o.id} nume={o.nume} valoare={o.deschise} max={max} />
                    ))}
                  {n('de_preluat') > 0 && <Bara nume={null} valoare={n('de_preluat')} max={max} />}
                </div>
              </article>
            )
          })}
        </div>
      </StareLista>
    </div>
  )
}

function Cifra({
  eticheta,
  valoare,
  ton,
}: {
  eticheta: string
  valoare: number
  ton?: 'rosu' | 'galben'
}) {
  return (
    <div className="grid gap-0.5 rounded-md bg-b4l-bg-soft p-2">
      <span className="text-eticheta font-semibold uppercase tracking-wide text-b4l-text-dim">
        {eticheta}
      </span>
      <b
        className={`text-[17px] ${ton === 'rosu' ? 'text-b4l-red-dark' : ton === 'galben' ? 'text-b4l-amber-dark' : 'text-b4l-text'}`}
      >
        {valoare}
      </b>
    </div>
  )
}

function Bara({ nume, valoare, max }: { nume: string | null; valoare: number; max: number }) {
  return (
    <div className="grid grid-cols-[20px_minmax(0,1fr)_auto] items-center gap-2 text-mic">
      <Avatar nume={nume} />
      <div className="grid gap-0.5">
        <span className="truncate text-b4l-text-muted">{nume ?? 'De preluat'}</span>
        <div className="h-1.5 overflow-hidden rounded bg-b4l-bg">
          <span
            className={`block h-full ${nume ? 'bg-b4l-blue' : 'bg-b4l-amber'}`}
            style={{ width: `${(100 * valoare) / max}%` }}
          />
        </div>
      </div>
      <span className="tabular-nums text-b4l-text-dim">{valoare}</span>
    </div>
  )
}
